import { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TimeWheel } from "./time-wheel";
import { usePopoverPosition } from "@/components/ui/date-picker/use-popover-position";
import IcArrDate from "@/assets/icons/ic-arr-date.svg";

interface TimeWheelSelectorProps {
  hour: number;
  minute: number;
  second: number;
  isPM: boolean;
  hasTimeSelected?: boolean;
  onSubmit: (hour: number, minute: number, second: number, isPM: boolean) => void;
  onCancel: () => void;
}

export function TimeWheelSelector({
  hour,
  minute,
  second,
  isPM,
  hasTimeSelected = false,
  onSubmit,
  onCancel,
}: TimeWheelSelectorProps) {
  const [open, setOpen] = useState(false);
  const [stagedHour, setStagedHour] = useState(hour);
  const [stagedMinute, setStagedMinute] = useState(minute);
  const [stagedSecond, setStagedSecond] = useState(second);
  const [stagedIsPM, setStagedIsPM] = useState(isPM);
  const [hasInteracted, setHasInteracted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownSide = usePopoverPosition(triggerRef);

  // Sync staged values when dialog opens
  useEffect(() => {
    if (open) {
      setStagedHour(hour);
      setStagedMinute(minute);
      setStagedSecond(second);
      setStagedIsPM(isPM);
      setHasInteracted(hasTimeSelected);
    }
  }, [open, hour, minute, second, isPM, hasTimeSelected]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const formatTime = () => {
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(
      2,
      "0",
    )}:${String(second).padStart(2, "0")}`;
  };

  const handleSubmit = () => {
    onSubmit(stagedHour, stagedMinute, stagedSecond, stagedIsPM);
    setOpen(false);
  };

  const handleCancel = () => {
    onCancel();
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-[16px] py-[12px] rounded border border-[#00000027]"
      >
        <span
          className={`text-h4 ${hasTimeSelected ? "font-bold text-[#202020]" : "font-normal text-[#B0B0B0]"}`}
        >
          {formatTime()}
        </span>
        <div className="flex items-center justify-end gap-1">
          <span className="font-normal text-[#000000]">{isPM ? "PM" : "AM"}</span>
          <img src={IcArrDate} alt="arr-date" />
        </div>
      </button>

      {open && (
        <div
          className={`absolute top-[-28px] z-50 w-[396px] bg-white rounded shadow-lg border border-neutral-light/20 p-4`}
        >
          <p className="text-[20px] font-bold text-neutral-strong mb-4">Set time</p>

          <div className="bg-white rounded-lg mb-4">
            <div className="flex items-center justify-center gap-2">
              <TimeWheel value={stagedHour} max={11} onChange={(v) => { setStagedHour(v); setHasInteracted(true); }} />
              <div className="flex flex-col items-center">
                <div className="h-[48px] flex items-center justify-center w-[6px]">
                  <span className="text-h4 text-neutral-strong font-bold">:</span>
                </div>
              </div>
              <TimeWheel value={stagedMinute} max={59} onChange={(v) => { setStagedMinute(v); setHasInteracted(true); }} />
              <div className="flex flex-col items-center">
                <div className="h-[48px] flex items-center justify-center w-[6px]">
                  <span className="text-h4 text-neutral-strong font-bold">:</span>
                </div>
              </div>
              <TimeWheel value={stagedSecond} max={59} onChange={(v) => { setStagedSecond(v); setHasInteracted(true); }} />
              {/* AM/PM Toggle - aligned with current value line */}
              <div className="flex flex-col items-center flex-1 min-w-px">
                {/* AM - dimmed when PM selected */}
                <div className="h-[48px] flex items-center justify-center border-b border-neutral-light/20 w-full">
                  {/* <span className="text-h4 text-neutral-light font-normal">AM</span> */}
                </div>
                {/* Current selection - bold */}
                <button
                  onClick={() => { setStagedIsPM(!stagedIsPM); setHasInteracted(true); }}
                  className="h-[48px] flex items-center justify-center border-b border-neutral-light/20 w-full cursor-pointer transition-all duration-200 hover:bg-neutral/5"
                >
                  <span
                    className={`text-h4 font-bold ${
                      stagedIsPM ? "text-[#000000]" : "text-[#B0B0B0]"
                    }`}
                  >
                    {stagedIsPM ? "PM" : "AM"}
                  </span>
                </button>
                {/* PM - dimmed when AM selected */}
                <div
                  onClick={() => { setStagedIsPM(!stagedIsPM); setHasInteracted(true); }}
                  className="h-[48px] flex items-center justify-center w-full"
                >
                  <span className="text-[18px] text-[#8D8D8D] font-normal">
                    {!stagedIsPM ? "PM" : "AM"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Cancel and Submit buttons */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={handleCancel}
              className="h-[48px] px-5 py-3 rounded bg-[#EBEBEB] text-[#000000] font-bold text-[16px] hover:bg-neutral/10 transition-colors"
            >
              Cancel
            </button>
            <Button
              onClick={handleSubmit}
              disabled={!hasInteracted}
              className="h-[48px] px-5 py-3 rounded font-bold text-[16px]"
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
